import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { PresalesDeal } from '@/api/types'

const formatCurrency = (amount: number | null) => {
  if (!amount) return '-'
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Best-effort company name: scope customer name, or extract from deal name. */
const getCompanyName = (d: PresalesDeal): string => {
  if (d.company_name) return d.company_name
  if (d.scope_customer_name) return d.scope_customer_name
  const name = d.deal_name || ''
  const dash = name.indexOf(' - ')
  if (dash > 0) return name.substring(0, dash).trim()
  return name
}

/** Check if a deal stage contains "Contract Sent" (stages are prefixed e.g. "D5 - Contract Sent"). */
const isContractSentStage = (d: PresalesDeal): boolean => {
  const stage = (d.deal_stage || d.dealstage || '').toLowerCase()
  return stage.includes('contract sent')
}

/**
 * Filter deals eligible for commission recognition:
 * deal_stage contains "Contract Sent" OR deal is closed won.
 */
export function getCommissionDeals(deals: PresalesDeal[]): PresalesDeal[] {
  return deals.filter((d) => {
    const isClosedWon = !!(d.is_closed && d.closed_won)
    return isContractSentStage(d) || isClosedWon
  })
}

/**
 * Export a nicely formatted PDF of commission-eligible deals for a person.
 */
export function exportCommissionPDF(deals: PresalesDeal[], personName: string): void {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 15
  const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })

  // ── Header ──
  doc.setFillColor(17, 24, 39)
  doc.rect(0, 0, pageWidth, 42, 'F')

  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(255, 255, 255)
  doc.text('Commission Recognition Report', margin, 18)

  doc.setFontSize(12)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(200, 200, 200)
  doc.text(personName, margin, 28)

  doc.setFontSize(10)
  doc.setTextColor(160, 160, 160)
  doc.text(`Generated ${today}`, margin, 36)

  // ── Summary boxes ──
  const closedWon = deals.filter((d) => !!(d.is_closed && d.closed_won))
  const contractSent = deals.filter((d) => isContractSentStage(d) && !(d.is_closed && d.closed_won))
  const totalValue = deals.reduce((sum, d) => sum + (d.amount || 0), 0)

  let yPos = 52

  const boxWidth = (pageWidth - margin * 2 - 8) / 3
  const boxHeight = 22

  const drawBox = (x: number, count: string, label: string, color: number[]) => {
    doc.setFillColor(248, 250, 252)
    doc.setDrawColor(color[0], color[1], color[2])
    doc.setLineWidth(0.8)
    doc.roundedRect(x, yPos, boxWidth, boxHeight, 2, 2, 'FD')

    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(color[0], color[1], color[2])
    doc.text(count, x + boxWidth / 2, yPos + 10, { align: 'center' })

    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(100, 100, 100)
    doc.text(label, x + boxWidth / 2, yPos + 17, { align: 'center' })
  }

  drawBox(margin, deals.length.toString(), 'Total Deals', [59, 130, 246])
  drawBox(margin + boxWidth + 4, closedWon.length.toString(), 'Closed Won', [34, 197, 94])
  drawBox(margin + (boxWidth + 4) * 2, formatCurrency(totalValue), 'Total Value', [168, 85, 247])

  yPos += boxHeight + 12

  // ── Closed Won section ──
  if (closedWon.length > 0) {
    doc.setFontSize(13)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(34, 197, 94)
    doc.text('Closed Won', margin, yPos)
    yPos += 2

    autoTable(doc, {
      startY: yPos,
      margin: { left: margin, right: margin },
      head: [['Deal Name', 'Company', 'Amount', 'Close Date', 'Stage', 'Deal Owner']],
      body: closedWon.map((d) => [
        d.deal_name || '-',
        getCompanyName(d),
        formatCurrency(d.amount),
        formatDate(d.close_date),
        d.deal_stage || '-',
        d.deal_owner_name || '-',
      ]),
      styles: {
        fontSize: 9,
        cellPadding: 4,
        lineColor: [229, 231, 235],
        lineWidth: 0.3,
      },
      headStyles: {
        fillColor: [34, 197, 94],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        0: { cellWidth: 50 },
        2: { halign: 'right' },
        3: { cellWidth: 28 },
      },
    })

    yPos = (doc as any).lastAutoTable.finalY + 12
  }

  // ── Contract Sent section ──
  if (contractSent.length > 0) {
    // Check if we need a new page
    if (yPos > doc.internal.pageSize.getHeight() - 60) {
      doc.addPage()
      yPos = 20
    }

    doc.setFontSize(13)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(59, 130, 246)
    doc.text('Contract Sent', margin, yPos)
    yPos += 2

    autoTable(doc, {
      startY: yPos,
      margin: { left: margin, right: margin },
      head: [['Deal Name', 'Company', 'Amount', 'Close Date', 'Stage', 'Deal Owner']],
      body: contractSent.map((d) => [
        d.deal_name || '-',
        getCompanyName(d),
        formatCurrency(d.amount),
        formatDate(d.close_date),
        d.deal_stage || '-',
        d.deal_owner_name || '-',
      ]),
      styles: {
        fontSize: 9,
        cellPadding: 4,
        lineColor: [229, 231, 235],
        lineWidth: 0.3,
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        0: { cellWidth: 50 },
        2: { halign: 'right' },
        3: { cellWidth: 28 },
      },
    })

    yPos = (doc as any).lastAutoTable.finalY + 12
  }

  // ── Footer on every page ──
  const pageCount = doc.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    const pageHeight = doc.internal.pageSize.getHeight()
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(160, 160, 160)
    doc.text('FrankieOne — DealHub Commission Report', margin, pageHeight - 8)
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 8, { align: 'right' })
  }

  const fileName = `Commission_${personName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`
  doc.save(fileName)
}
